import { useState } from 'react';
import {
  Box, Typography, Stack, Select, MenuItem, FormControl,
  Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, Button,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import { useSearchParams } from 'react-router-dom';
import { colors } from '../theme/designTheme';
import AgentGraphPanel from './AgentGraphPanel';

const C = colors;

// ── Mock Data ─────────────────────────────────────────────────────────────────

const MOCK_FEEDBACK = [
  {
    id: 1,
    reportId: '#REP-8821',
    redflagId: 'FLAG-X90',
    feedbackContext:
      'Acme Corp is a business in stock trading, but agent identified it as a shell entity for crypto washing without verifying the L1 license.',
    from: { name: 'Agent_Davenport', initials: 'JD', color: '#3B82F6' },
    status: 'pending',
  },
  {
    id: 2,
    reportId: '#REP-8819',
    redflagId: 'FLAG-B04',
    feedbackContext:
      'Agent failed to cross-reference the LinkedIn staff count vs the official tax filing for CyberDyne systems. Discrepancy overlooked.',
    from: { name: 'Investigator_Kovacs', initials: 'MK', color: '#8B5CF6' },
    status: 'resolved',
  },
  {
    id: 3,
    reportId: '#REP-8815',
    redflagId: 'FLAG-Z22',
    feedbackContext:
      'Sentiment analysis on the leadership team was overly optimistic. Ignored three ongoing lawsuits in Singapore.',
    from: { name: 'Lead_Analyst_Sarah', initials: 'AS', color: '#EC4899' },
    status: 'pending',
  },
];

// ── Subcomponents ─────────────────────────────────────────────────────────────

const StatCard = ({ label, value, children }) => (
  <Box
    sx={{
      flex: 1,
      bgcolor: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 2,
      p: 2.5,
      minWidth: 0,
    }}
  >
    <Typography
      sx={{
        fontFamily: 'Lexend',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.1em',
        color: C.textSecondary,
        mb: 1,
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: 'Lexend',
        fontSize: 38,
        fontWeight: 700,
        color: C.textPrimary,
        lineHeight: 1,
        mb: 0.75,
      }}
    >
      {value}
    </Typography>
    {children}
  </Box>
);

const StatusPill = ({ status }) => {
  const isPending = status === 'pending';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor: isPending ? C.warning : C.accent,
          boxShadow: `0 0 6px ${isPending ? C.warning : C.accent}`,
        }}
      />
      <Typography
        sx={{
          fontFamily: 'Lexend',
          fontSize: 11,
          fontWeight: 600,
          letterSpacing: '0.08em',
          color: isPending ? C.warning : C.accent,
        }}
      >
        {isPending ? 'PENDING' : 'RESOLVED'}
      </Typography>
    </Box>
  );
};

const FlagChip = ({ label }) => (
  <Box
    sx={{
      display: 'inline-block',
      px: 1,
      py: 0.35,
      bgcolor: C.criticalBg,
      border: `1px solid ${C.criticalBorder}`,
      borderRadius: '5px',
      fontFamily: 'Lexend',
      fontSize: 11,
      fontWeight: 600,
      color: C.critical,
      letterSpacing: '0.04em',
      whiteSpace: 'nowrap',
    }}
  >
    {label}
  </Box>
);

// ── Feedback Review Page ───────────────────────────────────────────────────────

const FeedbackReview = () => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [feedbackData, setFeedbackData] = useState(MOCK_FEEDBACK);

  const filtered =
    statusFilter === 'all'
      ? feedbackData
      : feedbackData.filter((r) => r.status === statusFilter);

  const handleAccept = (id) =>
    setFeedbackData((prev) =>
      prev.map((r) => (r.id === id ? { ...r, status: 'resolved' } : r))
    );
  const handleReject = (id) =>
    setFeedbackData((prev) => prev.filter((r) => r.id !== id));

  const thSx = {
    fontFamily: 'Lexend',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.1em',
    color: C.textSecondary,
    bgcolor: C.surfaceElevated,
    borderBottom: `1px solid ${C.border}`,
    py: 1.5,
    px: 2,
  };
  const tdSx = {
    borderBottom: `1px solid ${C.borderSubtle}`,
    py: 2,
    px: 2,
  };

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100vh', p: 3 }}>
      {/* Breadcrumb */}
      <Typography
        sx={{
          fontFamily: 'Lexend',
          fontSize: 11,
          fontWeight: 500,
          letterSpacing: '0.12em',
          color: C.textSecondary,
          mb: 0.75,
        }}
      >
        ADMINISTRATION / INTEL_COLLECTION
      </Typography>

      {/* Header + Actions */}
      <Stack
        direction="row"
        alignItems="flex-start"
        justifyContent="space-between"
        mb={3}
      >
        <Typography
          sx={{
            fontFamily: 'Lexend',
            fontSize: 30,
            fontWeight: 700,
            letterSpacing: '0.06em',
            color: C.textPrimary,
          }}
        >
          FEEDBACK REVIEW
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <Button
            startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />}
            variant="outlined"
            sx={{
              fontFamily: 'Lexend',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.08em',
              color: C.textSecondary,
              borderColor: C.border,
              px: 2,
              '&:hover': { borderColor: C.accent, color: C.accent },
            }}
          >
            EXPORT LOGS
          </Button>
          <Button
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            variant="contained"
            sx={{
              fontFamily: 'Lexend',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.08em',
              bgcolor: C.accent,
              color: '#000',
              px: 2,
              '&:hover': { bgcolor: '#00B8D9' },
            }}
          >
            MANUAL REFRESH
          </Button>
        </Stack>
      </Stack>

      {/* Stat Cards */}
      <Stack direction="row" spacing={2} mb={3}>
        <StatCard label="TOTAL PENDING" value="1,284">
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <TrendingUpIcon sx={{ fontSize: 13, color: C.critical }} />
            <Typography
              sx={{
                fontFamily: 'Lexend',
                fontSize: 10,
                fontWeight: 600,
                color: C.critical,
                letterSpacing: '0.08em',
              }}
            >
              +12% VS LAST CYCLE
            </Typography>
          </Stack>
        </StatCard>

        <StatCard label="AVG. RESPONSE TIME" value="4.2M">
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <ShieldOutlinedIcon sx={{ fontSize: 13, color: C.accent }} />
            <Typography
              sx={{
                fontFamily: 'Lexend',
                fontSize: 10,
                fontWeight: 600,
                color: C.accent,
                letterSpacing: '0.08em',
              }}
            >
              OPTIMAL THRESHOLD
            </Typography>
          </Stack>
        </StatCard>

        <StatCard label="REVIEW ACCURACY" value="98.4%">
          <Box
            sx={{
              mt: 1,
              height: 4,
              borderRadius: 2,
              bgcolor: C.borderSubtle,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: '98.4%',
                height: '100%',
                bgcolor: C.accent,
                borderRadius: 2,
              }}
            />
          </Box>
        </StatCard>

        <StatCard label="ACTIVE INVESTIGATORS" value="42">
          <Stack direction="row" spacing={0.5} mt={0.75} alignItems="center">
            {[
              { init: 'JD', color: '#3B82F6' },
              { init: 'MK', color: '#8B5CF6' },
              { init: 'AS', color: '#EC4899' },
            ].map(({ init, color }) => (
              <Box
                key={init}
                sx={{
                  width: 22,
                  height: 22,
                  borderRadius: '50%',
                  bgcolor: color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontFamily: 'Lexend',
                  fontSize: 9,
                  fontWeight: 700,
                  color: '#fff',
                  border: `1.5px solid ${C.surface}`,
                  ml: '-4px',
                  '&:first-of-type': { ml: 0 },
                }}
              >
                {init}
              </Box>
            ))}
            <Box
              sx={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                bgcolor: C.surfaceElevated,
                border: `1.5px solid ${C.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'Lexend',
                fontSize: 8,
                fontWeight: 600,
                color: C.textSecondary,
                ml: '-4px',
              }}
            >
              +39
            </Box>
          </Stack>
        </StatCard>
      </Stack>

      {/* Table Container */}
      <Box
        sx={{
          bgcolor: C.surface,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        {/* Filter Row */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, py: 1.75, borderBottom: `1px solid ${C.border}` }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography
              sx={{
                fontFamily: 'Lexend',
                fontSize: 11,
                fontWeight: 600,
                color: C.textSecondary,
                letterSpacing: '0.08em',
              }}
            >
              FILTER:
            </Typography>
            <FormControl size="small">
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                sx={{
                  fontFamily: 'Lexend',
                  fontSize: 12,
                  fontWeight: 600,
                  color: C.textPrimary,
                  bgcolor: C.surfaceElevated,
                  minWidth: 140,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: C.accent },
                  '& .MuiSvgIcon-root': { color: C.textSecondary },
                }}
                MenuProps={{
                  PaperProps: {
                    sx: {
                      bgcolor: C.surfaceElevated,
                      border: `1px solid ${C.border}`,
                      '& .MuiMenuItem-root': {
                        fontFamily: 'Lexend',
                        fontSize: 12,
                        color: C.textPrimary,
                        '&:hover': { bgcolor: C.surfaceHover },
                        '&.Mui-selected': { bgcolor: C.accentDim },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="all">ALL STATUS</MenuItem>
                <MenuItem value="pending">PENDING</MenuItem>
                <MenuItem value="resolved">RESOLVED</MenuItem>
              </Select>
            </FormControl>
          </Stack>
          <Typography
            sx={{
              fontFamily: 'Lexend',
              fontSize: 11,
              color: C.textSecondary,
              letterSpacing: '0.06em',
            }}
          >
            DISPLAYING {filtered.length} OF 1,284 ENTRIES
          </Typography>
        </Stack>

        {/* Table */}
        <Table>
          <TableHead>
            <TableRow>
              {['REPORT ID', 'REDFLAG ID', 'FEEDBACK CONTEXT', 'FROM', 'STATUS', 'ACTIONS'].map(
                (col) => (
                  <TableCell key={col} sx={thSx}>
                    {col}
                  </TableCell>
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.map((row) => (
              <TableRow
                key={row.id}
                sx={{ '&:hover': { bgcolor: C.surfaceHover } }}
              >
                <TableCell sx={tdSx}>
                  <Typography
                    sx={{
                      fontFamily: 'Lexend',
                      fontSize: 12,
                      fontWeight: 600,
                      color: C.textPrimary,
                    }}
                  >
                    {row.reportId}
                  </Typography>
                </TableCell>

                <TableCell sx={tdSx}>
                  <FlagChip label={row.redflagId} />
                </TableCell>

                <TableCell sx={{ ...tdSx, maxWidth: 380 }}>
                  <Typography
                    sx={{
                      fontFamily: 'Lexend',
                      fontSize: 12,
                      fontStyle: 'italic',
                      color: C.textSecondary,
                      lineHeight: 1.65,
                    }}
                  >
                    "{row.feedbackContext}"
                  </Typography>
                </TableCell>

                <TableCell sx={tdSx}>
                  <Stack direction="row" alignItems="center" spacing={1.25}>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: '50%',
                        bgcolor: row.from.color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: 'Lexend',
                        fontSize: 10,
                        fontWeight: 700,
                        color: '#fff',
                        flexShrink: 0,
                      }}
                    >
                      {row.from.initials}
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: 'Lexend',
                        fontSize: 12,
                        color: C.textPrimary,
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.from.name}
                    </Typography>
                  </Stack>
                </TableCell>

                <TableCell sx={tdSx}>
                  <StatusPill status={row.status} />
                </TableCell>

                <TableCell sx={tdSx}>
                  {row.status === 'pending' ? (
                    <Stack direction="row" spacing={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => handleAccept(row.id)}
                        sx={{
                          color: C.low,
                          border: `1px solid ${C.lowBorder}`,
                          borderRadius: '6px',
                          p: 0.5,
                          '&:hover': { bgcolor: C.lowBg },
                        }}
                      >
                        <CheckIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleReject(row.id)}
                        sx={{
                          color: C.critical,
                          border: `1px solid ${C.criticalBorder}`,
                          borderRadius: '6px',
                          p: 0.5,
                          '&:hover': { bgcolor: C.criticalBg },
                        }}
                      >
                        <CloseIcon sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Stack>
                  ) : (
                    <IconButton
                      size="small"
                      sx={{
                        color: C.accent,
                        border: `1px solid ${C.border}`,
                        borderRadius: '6px',
                        p: 0.5,
                        '&:hover': { bgcolor: C.accentDim },
                      }}
                    >
                      <VisibilityIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* Pagination */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, py: 1.75, borderTop: `1px solid ${C.border}` }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              sx={{
                fontFamily: 'Lexend',
                fontSize: 11,
                fontWeight: 600,
                color: C.textSecondary,
                letterSpacing: '0.06em',
                '&:hover': { color: C.textPrimary },
              }}
            >
              PREV
            </Button>
            {[1, 2, 3].map((n) => (
              <Box
                key={n}
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1,
                  bgcolor: n === 1 ? C.accent : 'transparent',
                  border: `1px solid ${n === 1 ? C.accent : C.border}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  '&:hover': { borderColor: C.accent },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Lexend',
                    fontSize: 12,
                    fontWeight: 600,
                    color: n === 1 ? '#000' : C.textSecondary,
                  }}
                >
                  {String(n).padStart(2, '0')}
                </Typography>
              </Box>
            ))}
            <Button
              size="small"
              sx={{
                fontFamily: 'Lexend',
                fontSize: 11,
                fontWeight: 600,
                color: C.textSecondary,
                letterSpacing: '0.06em',
                '&:hover': { color: C.textPrimary },
              }}
            >
              NEXT
            </Button>
          </Stack>
          <Typography
            sx={{
              fontFamily: 'Lexend',
              fontSize: 10,
              color: C.textMuted,
              letterSpacing: '0.08em',
            }}
          >
            INTELLIGENCE_LAYER_V2.4.0
          </Typography>
        </Stack>
      </Box>
    </Box>
  );
};

// ── Admin ─────────────────────────────────────────────────────────────────────

const Admin = () => {
  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'feedback';

  if (tab === 'agents') return <AgentGraphPanel />;
  return <FeedbackReview />;
};

export default Admin;
